import { useState } from 'react';

import { CAPTURE_LABEL, CONFIDENCE_LABEL, fmtDate, fmtTime } from '../../lib/format';
import type { TimelineItem } from '../../lib/types';
import { Empty, Icon } from './bits';

type Tone = 'risk' | 'ok' | 'accent' | undefined;

export const describe = (t: Pick<TimelineItem, 'kind' | 'section' | 'data'>): { icon: string; text: string; tone?: Tone } => {
  const d = t.data ?? {};
  switch (t.kind) {
    case 'login':
      return { icon: 'key', text: 'Entrou com o código' };
    case 'open':
      return { icon: 'eye', text: d.device ? `Abriu a proposta · ${d.device}${d.browser ? `, ${d.browser}` : ''}` : 'Abriu a proposta' };
    case 'section':
      return { icon: 'pin', text: `Chegou em ${t.section ?? 'uma nova seção'}` };
    case 'scroll':
      return { icon: 'down', text: Number(d.pct) >= 100 ? 'Chegou ao fim da página' : `Rolou ${d.pct}% da página` };
    case 'capture':
      return {
        icon: 'cam',
        tone: 'risk',
        text: `${CAPTURE_LABEL[String(d.type)] ?? 'Sinal de captura'} · ${CONFIDENCE_LABEL[String(d.confidence)] ?? ''}${d.chars ? ` · ${d.chars} caracteres` : ''}`,
      };
    case 'click':
      return { icon: 'tap', text: `Clicou em “${d.text ?? ''}”` };
    case 'accept':
      return { icon: 'ok', tone: 'ok', text: 'Aceitou a proposta' };
    case 'adjust':
      return { icon: 'edit', tone: 'accent', text: 'Pediu um ajuste' };
    case 'meeting':
      return { icon: 'cal', tone: 'accent', text: 'Pediu uma reunião' };
    case 'renewal':
      return { icon: 'renew', tone: 'accent', text: 'Pediu renovação' };
    default:
      return { icon: 'live', text: t.kind };
  }
};

const NOISY = new Set(['click', 'section']);

export const Timeline = ({ items, showEmail = true }: { items: TimelineItem[]; showEmail?: boolean }) => {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.filter((i) => !NOISY.has(i.kind));
  const groups: Array<{ day: string; rows: TimelineItem[] }> = [];
  for (const it of shown.slice(0, 250)) {
    const day = fmtDate(it.at);
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.rows.push(it);
    else groups.push({ day, rows: [it] });
  }

  return (
    <div className="tl">
      <div className="seg seg-sm" role="group" aria-label="Filtrar eventos">
        <button type="button" aria-pressed={!all} onClick={() => setAll(false)}>
          Principais
        </button>
        <button type="button" aria-pressed={all} onClick={() => setAll(true)}>
          Tudo, com cliques e seções
        </button>
      </div>
      {groups.length === 0 ? (
        <Empty>Nada por aqui ainda.</Empty>
      ) : (
        groups.map((g) => (
          <div key={g.day} className="tl-day">
            <p className="tl-date">{g.day}</p>
            <ol>
              {g.rows.map((it, i) => {
                const d = describe(it);
                return (
                  <li key={i} data-tone={d.tone}>
                    <span className="tl-ic">
                      <Icon name={d.icon} />
                    </span>
                    <div className="tl-tx">
                      <p>{d.text}</p>
                      <p className="tl-meta">
                        <span className="num">{fmtTime(it.at)}</span>
                        {showEmail && <span>{it.email}</span>}
                        {it.section && it.kind !== 'section' && <span>em {it.section}</span>}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))
      )}
    </div>
  );
};
